import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Users, Globe, MessageSquare, Plus, Heart, X, Hash, Lock, Trash2, MoreVertical, Image, FileText, Youtube, Link as LinkIcon, Edit2, ArrowLeft, Sparkles, Flame, TrendingUp, ChevronDown, Flag, Search, Loader2 } from 'lucide-react';
import api from '../api';
import '../components/Discussions.css';
import ReportModal from '../components/ReportModal';
import { useRef } from 'react';
import StudentLayout from '../components/StudentLayout';
import { useFeatures } from '../components/FeatureGate';

const RichText = ({ text, onHashtagClick }) => {
    if (!text) return null;
    
    // Regex to split text by hashtags and mentions
    const parts = text.split(/(#\w+|@\w+)/g);
    
    return (
        <>
            {parts.map((part, i) => {
                if (part.startsWith('#')) {
                    return (
                        <span 
                            key={i} 
                            className="hashtag-link" 
                            onClick={(e) => {
                                e.stopPropagation();
                                onHashtagClick(part.substring(1));
                            }}
                        >
                            {part}
                        </span>
                    );
                } else if (part.startsWith('@')) {
                    return (
                        <span key={i} className="mention-tag">
                            {part}
                        </span>
                    );
                }
                return part;
            })}
        </>
    );
};

const MentionDropdown = ({ results, onSelect }) => {
    if (!results || results.length === 0) return null;
    return (
        <div className="mention-dropdown">
            {results.map(u => (
                <div key={u.id} className="mention-item" onClick={() => onSelect(u)}>
                    <div className="mention-avatar">{u.name.charAt(0)}</div>
                    <div className="mention-name">{u.name} <small>({u.type})</small></div>
                </div>
            ))}
        </div>
    );
};

const Avatar = ({ src, name }) => {
    const [imgError, setImgError] = useState(false);

    if (src && !imgError) {
        return (
            <img
                src={src}
                alt={name}
                className="feed-avatar"
                onError={() => setImgError(true)}
            />
        );
    }

    return (
        <div className="feed-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary)', color: '#0B0F14', fontWeight: 'bold' }}>
            {name ? name.charAt(0).toUpperCase() : '?'}
        </div>
    );
};

const CommentItem = ({ 
    comment, 
    postId, 
    userId, 
    isGroupAdmin, 
    handleDeleteComment, 
    handleAddComment, 
    newComment, 
    handleContentChange, 
    fetchComments, 
    loadingComments,
    onHashtagClick,
    showMentionDropdown,
    mentionResults,
    insertMention,
    activeInputType,
    activeCommentId,
    onReportComment,
    canPost = true
}) => {
    const [isReplying, setIsReplying] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showMenu, setShowMenu] = useState(false);

    const toggleReplies = () => {
        if (!isExpanded && (!comment.replies || comment.replies.length === 0)) {
            fetchComments(postId, comment.id);
        }
        setIsExpanded(!isExpanded);
    };

    return (
        <div className="comment-thread">
            <div className="comment-item">
                <Avatar src={comment.user_avatar} name={comment.user_name} />
                <div className="comment-bubble">
                    <div className="comment-bubble-header">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className="comment-author">{comment.user_name}</div>
                            <div className="comment-time">{new Date(comment.created_at).toLocaleString()}</div>
                        </div>
                        <div className="comment-menu-container">
                            <button 
                                className="comment-action-btn" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowMenu(!showMenu);
                                }}
                                title="More options"
                            >
                                <MoreVertical size={14} />
                            </button>
                            {showMenu && (
                                <div className="comment-dropdown-menu">
                                    {(comment.user_id === userId || isGroupAdmin) && (
                                        <button 
                                            className="dropdown-item delete-opt" 
                                            onClick={() => {
                                                setShowMenu(false);
                                                handleDeleteComment(postId, comment.id);
                                            }}
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    )}
                                    {onReportComment && (
                                        <button 
                                            className="dropdown-item report-opt" 
                                            onClick={() => {
                                                setShowMenu(false);
                                                onReportComment(comment.id, comment.parent_id ? 'reply' : 'comment');
                                            }}
                                        >
                                            <Flag size={14} /> Report
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="comment-text">
                        <RichText text={comment.content} onHashtagClick={onHashtagClick} />
                    </div>
                    <div className="comment-footer">
                        <button className="comment-footer-btn" onClick={() => setIsReplying(!isReplying)}>Reply</button>
                        {comment.replies_count > 0 && (
                            <button className="comment-footer-btn" onClick={toggleReplies}>
                                {isExpanded ? 'Hide Replies' : `View ${comment.replies_count} Replies`}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {isReplying && canPost && (
                <div className="reply-input-wrapper" style={{ position: 'relative' }}>
                    <input 
                        type="text" 
                        className="comment-input reply-input" 
                        placeholder={`Reply to ${comment.user_name}...`} 
                        value={newComment[`reply-${comment.id}`] || ''} 
                        onChange={(e) => handleContentChange(e.target.value, 'comment', `reply-${comment.id}`)} 
                        onKeyPress={(e) => { 
                            if (e.key === 'Enter') {
                                handleAddComment(postId, comment.id);
                                setIsReplying(false);
                                setIsExpanded(true);
                            } 
                        }} 
                    />
                    {showMentionDropdown && activeInputType === 'comment' && activeCommentId === `reply-${comment.id}` && (
                        <MentionDropdown results={mentionResults} onSelect={insertMention} />
                    )}
                    <button onClick={() => setIsReplying(false)} className="cancel-reply">Cancel</button>
                </div>
            )}

            {isExpanded && (
                <div className="nested-replies">
                    {loadingComments[comment.id] ? (
                        <div className="loading-replies">Loading...</div>
                    ) : (
                        (comment.replies || []).map(reply => (
                            <CommentItem 
                                key={reply.id} 
                                comment={reply} 
                                postId={postId} 
                                userId={userId} 
                                isGroupAdmin={isGroupAdmin}
                                handleDeleteComment={handleDeleteComment}
                                handleAddComment={handleAddComment}
                                newComment={newComment}
                                handleContentChange={handleContentChange}
                                fetchComments={fetchComments}
                                loadingComments={loadingComments}
                                onHashtagClick={onHashtagClick}
                                showMentionDropdown={showMentionDropdown}
                                mentionResults={mentionResults}
                                insertMention={insertMention}
                                activeInputType={activeInputType}
                                activeCommentId={activeCommentId}
                                onReportComment={onReportComment}
                                canPost={canPost}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

const DiscussionsPage = ({ embedded = false }) => {
    const { isSubEnabled } = useFeatures();
    // Sub-feature: institutions can allow viewing the feed but disable posting.
    const canPost = isSubEnabled('discussions', 'groups_post');
    const [activeTab, setActiveTab] = useState('global'); // 'global' or 'group_ID'
    const [showGroupDropdown, setShowGroupDropdown] = useState(false);
    const [posts, setPosts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [newPostContent, setNewPostContent] = useState("");
    const [role, setRole] = useState(null);
    const navigate = useNavigate();

    // Group Management
    const [userId, setUserId] = useState(null);
    const [requests, setRequests] = useState([]);

    // Create Group Form
    const [newGroupData, setNewGroupData] = useState({ name: '', description: '', is_private: false, batch: '' });
    const [availableBatches, setAvailableBatches] = useState([]);

    // Comments State
    const [expandedComments, setExpandedComments] = useState({}); // { postId: true/false }
    const [comments, setComments] = useState({}); // { postId: [comments] }
    const [newComment, setNewComment] = useState({}); // { postId: "comment text" }
    const [loadingComments, setLoadingComments] = useState({});

    // Group Admin Modal
    const [showManageGroup, setShowManageGroup] = useState(false);
    const [groupMembers, setGroupMembers] = useState([]);

    // Report Modal & Post Footer Menu State
    const [openPostMenuId, setOpenPostMenuId] = useState(null);
    const [reportModalConfig, setReportModalConfig] = useState({ isOpen: false, contentType: 'post', contentId: null });

    const openReportModal = (contentType, contentId) => {
        setReportModalConfig({ isOpen: true, contentType, contentId });
        setOpenPostMenuId(null);
    };

    // Toast State
    const [toasts, setToasts] = useState([]);
    const [activeHashtag, setActiveHashtag] = useState(null);
    const [sortBy, setSortBy] = useState('new');
    const [trendingTags, setTrendingTags] = useState([]);

    // Pagination & Search State
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [groupSearch, setGroupSearch] = useState('');
    const POSTS_PER_PAGE = 10;
    const loaderRef = useRef(null);

    const filteredGroups = useMemo(() => {
        if (!groupSearch.trim()) return groups;
        const q = groupSearch.toLowerCase().trim();
        return groups.filter(g => 
            (g.name && g.name.toLowerCase().includes(q)) || 
            (g.batch && g.batch.toLowerCase().includes(q)) ||
            (g.description && g.description.toLowerCase().includes(q))
        );
    }, [groups, groupSearch]);

    // Mention State
    const [mentionSearch, setMentionSearch] = useState("");
    const [mentionResults, setMentionResults] = useState([]);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
    const [activeInputType, setActiveInputType] = useState(null); // 'post' or 'comment'
    const [activeCommentId, setActiveCommentId] = useState(null);

    // Edit/Delete State
    const [editingPostId, setEditingPostId] = useState(null);
    const [editContent, setEditContent] = useState("");
    const [activeMenuId, setActiveMenuId] = useState(null);

    // Media State for Post Creation
    const [mediaType, setMediaType] = useState(null); // 'image', 'pdf', 'youtube', 'link'
    const [mediaUrl, setMediaUrl] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [linkPreview, setLinkPreview] = useState(null);
    const [isFetchingLink, setIsFetchingLink] = useState(false);
    const [isSubmittingPost, setIsSubmittingPost] = useState(false);

    const showToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        onConfirm: null,
        isDanger: false
    });

    const closeConfirm = () => setConfirmModal(prev => ({ ...prev, show: false }));

    const fetchGroups = useCallback(async () => {
        try {
            const res = await api.get('/discussions/groups');
            setGroups(res.data);
        } catch (err) {
            console.error("Error fetching groups:", err);
        }
    }, []);

    const fetchPosts = useCallback(async (isLoadMore = false) => {
        if (!isLoadMore) {
            setLoading(true);
            setPage(0);
        } else {
            setIsLoadingMore(true);
        }
        
        try {
            let url = '/discussions/feed';
            const skip = isLoadMore ? (page + 1) * POSTS_PER_PAGE : 0;
            
            if (activeTab !== 'global') {
                url = `/discussions/groups/${activeTab}/posts`;
            }
            
            const params = { skip, limit: POSTS_PER_PAGE, sort_by: sortBy };
            if (activeHashtag) params.hashtag = activeHashtag;

            const res = await api.get(url, { params });
            
            if (isLoadMore) {
                setPosts(prev => [...prev, ...res.data]);
                setPage(prev => prev + 1);
            } else {
                setPosts(res.data);
            }
            
            setHasMore(res.data.length === POSTS_PER_PAGE);
        } catch (err) {
            console.error("Error fetching posts:", err);
            if (err.response && err.response.status === 403) {
                setPosts([]);
            }
        } finally {
            setLoading(false);
            setIsLoadingMore(false);
        }
    }, [activeTab, activeHashtag, sortBy, page]);

    // Infinite Scroll Observer
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                fetchPosts(true);
            }
        }, { threshold: 1.0 });

        if (loaderRef.current) {
            observer.observe(loaderRef.current);
        }

        return () => observer.disconnect();
    }, [hasMore, loading, fetchPosts]);

    const fetchTrendingTags = useCallback(async () => {
        try {
            const res = await api.get('/discussions/hashtags/trending');
            setTrendingTags(res.data);
        } catch (err) {
            console.error("Error fetching trending tags:", err);
        }
    }, []);

    const fetchBatches = useCallback(async () => {
        try {
            const res = await api.get('/discussions/batches');
            setAvailableBatches(res.data || []);
        } catch (err) {
            console.error("Error fetching batches:", err);
        }
    }, []);

    useEffect(() => {
        const initUser = async () => {
            let storedUserId = localStorage.getItem('user_id');
            const userRole = localStorage.getItem('role');
            setRole(userRole);

            if (!storedUserId) {
                try {
                    const res = await api.get('/auth/me');
                    storedUserId = res.data.id;
                    localStorage.setItem('user_id', storedUserId);
                } catch (e) {
                    console.error("Failed to fetch user info", e);
                }
            }
            if (storedUserId) setUserId(parseInt(storedUserId));
        };

        initUser();
        fetchGroups();
        fetchPosts();
        fetchTrendingTags();
        fetchBatches();
    }, [activeTab, fetchGroups, fetchPosts, fetchTrendingTags, fetchBatches]);

    useEffect(() => {
        if (!embedded) {
            const userRole = localStorage.getItem('role') || '';
            if (userRole.toLowerCase() === 'university') {
                navigate('/uni-dashboard', { replace: true, state: { showDiscussions: true } });
            } else if (userRole.toLowerCase() === 'student') {
                navigate('/student-dashboard', { replace: true, state: { showDiscussions: true } });
            } else if (userRole.toLowerCase() === 'pop' || userRole.toLowerCase() === 'professor') {
                navigate('/pop-dashboard', { replace: true, state: { showDiscussions: true } });
            } else if (userRole.toLowerCase() === 'faculty') {
                navigate('/faculty-dashboard', { replace: true, state: { showDiscussions: true } });
            }
        }
    }, [embedded, navigate]);

    const fetchRequests = useCallback(async (groupId) => {
        try {
            const res = await api.get(`/discussions/groups/${groupId}/requests`);
            setRequests(res.data);
        } catch (err) {
            console.error("Error fetching requests:", err);
        }
    }, []);

    useEffect(() => {
        if (activeTab !== 'global') {
            const group = groups.find(g => g.id === parseInt(activeTab) || g.id === activeTab);
            if (group && Number(group.created_by) === Number(userId) && group.is_private) {
                fetchRequests(group.id);
            } else {
                setRequests([]);
            }
        }
    }, [activeTab, groups, userId, fetchRequests]);

    const handleBack = () => {
        const normalizedRole = role ? role.toLowerCase() : '';
        if (normalizedRole === 'university') navigate('/uni-dashboard');
        else if (normalizedRole === 'pop') navigate('/pop-dashboard');
        else if (normalizedRole === 'student') navigate('/student-dashboard');
        else navigate('/');
    };

    const handleRequestAction = async (requestId, action) => {
        try {
            await api.post(`/discussions/groups/${activeTab}/requests/${requestId}/${action}`);
            setRequests(requests.filter(r => r.request_id !== requestId));
            showToast(`Request ${action}d successfully`, 'success');
            if (action === 'approve') fetchGroups();
        } catch (err) {
            console.error(`Error ${action}ing request:`, err);
            showToast(`Failed to ${action} request`, 'error');
        }
    };

    const handleCreatePost = async () => {
        if (!newPostContent.trim() && !mediaUrl) return;
        if (isSubmittingPost) return;
        
        setIsSubmittingPost(true);
        try {
            const payload = {
                content: newPostContent,
                group_id: activeTab === 'global' ? null : parseInt(activeTab),
                media_url: mediaUrl,
                media_type: mediaType,
                link_preview_data: linkPreview
            };
            const res = await api.post('/discussions/posts', payload);
            setPosts([res.data, ...posts]);
            setNewPostContent("");
            setMediaUrl("");
            setMediaType(null);
            setLinkPreview(null);
            showToast("Post created successfully", 'success');
        } catch (err) {
            console.error("Error creating post:", err);
            const errorMsg = err.response?.data?.detail || "Failed to create post";
            showToast(errorMsg, 'error');
        } finally {
            setIsSubmittingPost(false);
        }
    };

    const handleDeletePost = (postId) => {
        setConfirmModal({
            show: true,
            title: 'Delete Post',
            message: 'Are you sure you want to delete this post? This action cannot be undone.',
            isDanger: true,
            onConfirm: async () => {
                try {
                    await api.delete(`/discussions/posts/${postId}`);
                    setPosts(posts.filter(p => p.id !== postId));
                    showToast("Post deleted", 'success');
                } catch (err) {
                    console.error("Error deleting post:", err);
                    showToast("Failed to delete post", 'error');
                } finally {
                    closeConfirm();
                }
            }
        });
    };

    const handleStartEdit = (post) => {
        setEditingPostId(post.id);
        setEditContent(post.content);
        setActiveMenuId(null);
    };

    const handleSaveEdit = async (postId) => {
        try {
            const res = await api.put(`/discussions/posts/${postId}`, { content: editContent });
            setPosts(posts.map(p => p.id === postId ? res.data : p));
            setEditingPostId(null);
            showToast("Post updated", 'success');
        } catch (err) {
            console.error("Error updating post:", err);
            showToast("Failed to update post", 'error');
        }
    };

    const handleFileUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // Size validation
        if (type === 'image' && file.size > 2 * 1024 * 1024) {
            showToast("Image size must be less than 2 MB.", 'error');
            // Clear the file input
            e.target.value = null;
            return;
        }
        if (type === 'pdf' && file.size > 10 * 1024 * 1024) {
            showToast("Document size must be less than 10 MB.", 'error');
            e.target.value = null;
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await api.post('/discussions/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setMediaUrl(res.data.file_path);
            setMediaType(type);
            showToast(`${type.toUpperCase()} uploaded`, 'success');
        } catch (err) {
            console.error("Upload error:", err);
            showToast("Failed to upload file", 'error');
        } finally {
            setIsUploading(false);
        }
    };

    const handleLinkDetection = async (text) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const matches = text.match(urlRegex);
        if (matches && matches.length > 0) {
            const url = matches[0];
            // Skip if already have media or same link
            if (mediaUrl || (linkPreview && linkPreview.url === url)) return;
            
            // Check for YouTube specifically
            const ytRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
            const ytMatch = url.match(ytRegex);
            if (ytMatch) {
                setMediaUrl(ytMatch[1]);
                setMediaType('youtube');
                return;
            }

            // Generic link preview
            setIsFetchingLink(true);
            try {
                const res = await api.post('/discussions/link-preview', { url });
                setLinkPreview(res.data);
                setMediaType('link');
                setMediaUrl(url);
            } catch (err) {
                console.error("Link preview error:", err);
            } finally {
                setIsFetchingLink(false);
            }
        }
    };

    const handleCreateGroup = async () => {
        try {
            const res = await api.post('/discussions/groups', newGroupData);
            setGroups([...groups, res.data]);
            setShowCreateGroup(false);
            setNewGroupData({ name: '', description: '', is_private: false, batch: '' });
            setActiveTab(res.data.id);
            showToast("Group created successfully", 'success');
        } catch (err) {
            console.error("Error creating group:", err);
            showToast("Failed to create group. Name might be taken.", 'error');
        }
    };

    const handleLike = async (postId) => {
        try {
            const res = await api.post(`/discussions/posts/${postId}/like`);
            setPosts(posts.map(p =>
                p.id === postId
                    ? { ...p, likes_count: res.data.likes_count, is_liked: res.data.is_liked }
                    : p
            ));
        } catch (err) {
            console.error("Error liking post:", err);
            showToast("Failed to update like status", 'error');
        }
    };

    const handleJoinGroup = async (groupId) => {
        try {
            const res = await api.post(`/discussions/groups/${groupId}/join`);
            setGroups(groups.map(g => {
                if (g.id === groupId) {
                    if (res.data.status === 'MEMBER') {
                        return { ...g, is_member: true, members_count: g.members_count + 1 };
                    }
                    if (res.data.status === 'REQUESTED') {
                        return { ...g, has_requested: true };
                    }
                }
                return g;
            }));
            if (res.data.status === 'MEMBER' && activeTab === groupId) {
                fetchPosts();
            }
            showToast(res.data.message, 'success');
        } catch (err) {
            console.error("Error joining group:", err);
            showToast("Failed to join group", 'error');
        }
    };

    const fetchComments = async (postId, parentId = null) => {
        setLoadingComments(prev => ({ ...prev, [parentId || postId]: true }));
        try {
            const params = parentId ? { parent_id: parentId } : {};
            const res = await api.get(`/discussions/posts/${postId}/comments`, { params });
            
            if (parentId) {
                setComments(prev => ({
                    ...prev,
                    [postId]: prev[postId].map(c => 
                        c.id === parentId ? { ...c, replies: res.data } : c
                    )
                }));
            } else {
                setComments(prev => ({ ...prev, [postId]: res.data }));
            }
        } catch (err) {
            console.error("Error fetching comments:", err);
            showToast("Failed to load comments", 'error');
        } finally {
            setLoadingComments(prev => ({ ...prev, [parentId || postId]: false }));
        }
    };

    const handleAddComment = async (postId, parentId = null) => {
        const inputKey = parentId ? `reply-${parentId}` : postId;
        const commentText = newComment[inputKey];
        if (!commentText || !commentText.trim()) return;

        try {
            const res = await api.post(`/discussions/posts/${postId}/comments`, {
                content: commentText,
                parent_id: parentId
            });

            if (parentId) {
                // Update parent replies
                setComments(prev => ({
                    ...prev,
                    [postId]: prev[postId].map(c => 
                        c.id === parentId ? { ...c, replies: [...(c.replies || []), res.data], replies_count: c.replies_count + 1 } : c
                    )
                }));
            } else {
                setComments(prev => ({
                    ...prev,
                    [postId]: [...(prev[postId] || []), res.data]
                }));
            }

            setPosts(posts.map(p =>
                p.id === postId ? { ...p, comments_count: p.comments_count + 1 } : p
            ));

            setNewComment(prev => ({ ...prev, [inputKey]: '' }));
            showToast("Comment added", 'success');
        } catch (err) {
            console.error("Error adding comment:", err);
            const errorMsg = err.response?.data?.detail || "Failed to add comment";
            showToast(errorMsg, 'error');
        }
    };

    const toggleComments = async (postId) => {
        const isExpanded = expandedComments[postId];
        setExpandedComments({ ...expandedComments, [postId]: !isExpanded });
        if (!isExpanded && !comments[postId]) {
            await fetchComments(postId);
        }
    };

    const handleHashtagClick = (tag) => {
        setActiveHashtag(tag === activeHashtag ? null : tag);
    };

    const handleContentChange = async (val, type, id = null) => {
        if (type === 'post') setNewPostContent(val);
        else setNewComment(prev => ({ ...prev, [id]: val }));

        // Match '@' optionally followed by word characters at end of string
        const lastWordMatch = val.match(/@(\w*)$/);

        if (lastWordMatch !== null) {
            const query = lastWordMatch[1]; // empty string when just '@' typed
            setActiveInputType(type);
            setActiveCommentId(id);
            try {
                const res = await api.get(`/discussions/users/search?q=${query}`);
                setMentionResults(res.data);
                setShowMentionDropdown(true);
            } catch (err) {
                console.error('Mention search error:', err);
            }
        } else {
            setShowMentionDropdown(false);
            setMentionResults([]);
        }
    };

    const insertMention = (user) => {
        const type = activeInputType;
        const id = activeCommentId;
        const currentVal = type === 'post' ? newPostContent : newComment[id];
        const newVal = currentVal.replace(/@\w*$/, `@${user.name} `);
        
        if (type === 'post') setNewPostContent(newVal);
        else setNewComment(prev => ({ ...prev, [id]: newVal }));
        
        setShowMentionDropdown(false);
    };

    const handleDeleteComment = (postId, commentId) => {
        setConfirmModal({
            show: true,
            title: 'Delete Comment',
            message: 'Are you sure you want to delete this comment? This action cannot be undone.',
            isDanger: true,
            onConfirm: async () => {
                try {
                    await api.delete(`/discussions/posts/${postId}/comments/${commentId}`);
                    setComments(prev => ({
                        ...prev,
                        [postId]: (prev[postId] || []).filter(c => c.id !== commentId)
                    }));

                    setPosts(posts.map(p =>
                        p.id === postId ? { ...p, comments_count: Math.max(0, p.comments_count - 1) } : p
                    ));
                    showToast("Comment deleted", 'success');
                } catch (err) {
                    console.error("Error deleting comment:", err);
                    showToast("Failed to delete comment", 'error');
                } finally {
                    closeConfirm();
                }
            }
        });
    };

    const handleManageGroup = async () => {
        const currentGroup = groups.find(g => g.id === parseInt(activeTab) || g.id === activeTab);
        if (!currentGroup) return;
        try {
            const res = await api.get(`/discussions/groups/${currentGroup.id}/members`);
            setGroupMembers(res.data);
            setShowManageGroup(true);
        } catch (err) {
            console.error("Error fetching members:", err);
        }
    };

    const handleRemoveMember = (memberId) => {
        const currentGroup = groups.find(g => g.id === parseInt(activeTab) || g.id === activeTab);
        setConfirmModal({
            show: true,
            title: 'Remove Member',
            message: 'Are you sure you want to remove this member from the group?',
            isDanger: true,
            onConfirm: async () => {
                try {
                    await api.delete(`/discussions/groups/${currentGroup.id}/members/${memberId}`);
                    setGroupMembers(groupMembers.filter(m => m.user_id !== memberId));
                    fetchGroups();
                    showToast("Member removed", 'success');
                } catch (err) {
                    console.error("Error removing member:", err);
                    showToast("Failed to remove member", 'error');
                } finally {
                    closeConfirm();
                }
            }
        });
    };

    const handleDeleteGroup = () => {
        const currentGroup = groups.find(g => g.id === parseInt(activeTab) || g.id === activeTab);
        setConfirmModal({
            show: true,
            title: 'Delete Group',
            message: `Are you sure you want to delete the group "${currentGroup.name}"? This will permanently delete all posts, comments, and memberships. This action is IRREVOCABLE.`,
            isDanger: true,
            onConfirm: async () => {
                try {
                    await api.delete(`/discussions/groups/${currentGroup.id}`);
                    setGroups(groups.filter(g => g.id !== currentGroup.id));
                    setActiveTab('global');
                    setShowManageGroup(false);
                    showToast("Group deleted successfully", 'success');
                } catch (err) {
                    console.error("Error deleting group:", err);
                    showToast("Failed to delete group", 'error');
                } finally {
                    closeConfirm();
                }
            }
        });
    };

    const currentGroup = groups.find(g => g.id === parseInt(activeTab) || g.id === activeTab);
    const isMemberOfCurrent = currentGroup?.is_member;
    const isGroupAdmin = Number(currentGroup?.created_by) === Number(userId);

    const content = (
            <div className="discussions-container" style={embedded ? { minHeight: 'calc(100vh - 120px)', width: '100%', background: 'transparent' } : {}}>
            {/* Multi-Toast Notifications */}
            <div className="toasts-wrapper">
                <AnimatePresence>
                    {toasts.map(t => (
                        <motion.div
                            key={t.id}
                            className={`toast-message ${t.type}`}
                            initial={{ opacity: 0, x: 50, scale: 0.9 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 20, scale: 0.9, transition: { duration: 0.2 } }}
                        >
                            <span className="toast-icon">
                                {t.type === 'success' ? '✓' : '✕'}
                            </span>
                            <span className="toast-text">{t.message}</span>
                            <button className="toast-close" onClick={() => setToasts(prev => prev.filter(item => item.id !== t.id))}>×</button>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Report Modal */}
            {reportModalConfig.isOpen && (
                <ReportModal
                    isOpen={reportModalConfig.isOpen}
                    onClose={() => setReportModalConfig({ isOpen: false, contentType: 'post', contentId: null })}
                    contentType={reportModalConfig.contentType}
                    contentId={reportModalConfig.contentId}
                    onSuccess={() => showToast("Report submitted successfully.", 'success')}
                />
            )}

            {/* Confirmation Modal */}
            <AnimatePresence>
                {confirmModal.show && (
                    <div className="modal-overlay" style={{ zIndex: 99999 }}>
                        <motion.div
                            className="modal-box confirm-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                        >
                            <div className="modal-header">
                                <h3>{confirmModal.title}</h3>
                                <button onClick={closeConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
                            </div>
                            <div className="modal-content">
                                <p style={{ color: 'var(--text-muted)', marginBottom: '24px', lineHeight: '1.5' }}>{confirmModal.message}</p>
                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={closeConfirm}
                                        style={{ background: 'var(--bg-soft)', color: 'var(--proto-white)', border: '1px solid var(--border)', padding: '10px 20px', borderRadius: '24px', cursor: 'pointer', fontWeight: '600' }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmModal.onConfirm}
                                        style={{ background: confirmModal.isDanger ? 'var(--red)' : 'var(--primary)', color: '#0B0F14', border: 'none', padding: '10px 24px', borderRadius: '24px', cursor: 'pointer', fontWeight: '800' }}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            {!embedded && (
                <div className="disc-sidebar">
                    <button
                        onClick={handleBack}
                        className="disc-menu-item back-btn"
                        style={{
                            width: '100%',
                            whiteSpace: 'nowrap',
                            fontWeight: '700',
                            padding: '12px 16px',
                            marginBottom: '24px',
                            justifyContent: 'center',
                            gap: '10px'
                        }}
                    >
                        <ArrowLeft size={16} /> Back to Dashboard
                    </button>
                    <div className="disc-menu-items">
                        <button
                            className={`disc-menu-item ${activeTab === 'global' ? 'active' : ''}`}
                            onClick={() => setActiveTab('global')}
                        >
                            <Globe size={20} />
                            Global Feed
                        </button>
                        <button
                            className="create-group-btn"
                            onClick={() => setShowCreateGroup(true)}
                        >
                            <Plus size={18} /> Create New Group
                        </button>
                    </div>

                    <div className="disc-groups-list">
                        <div className="sidebar-group-search">
                            <Search size={14} className="sidebar-search-icon" />
                            <input 
                                type="text" 
                                className="sidebar-group-search-input"
                                placeholder="Search groups..."
                                value={groupSearch}
                                onChange={(e) => setGroupSearch(e.target.value)}
                            />
                            {groupSearch && (
                                <button className="sidebar-search-clear" onClick={() => setGroupSearch('')}>
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                        <h3>Your Groups</h3>
                        {filteredGroups.filter(g => g.is_member).map(group => (
                            <button
                                key={group.id}
                                className={`disc-menu-item ${activeTab === group.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(group.id)}
                                style={{ justifyContent: 'space-between' }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {group.is_private ? <Lock size={16} /> : <Hash size={16} />}
                                    {group.name}
                                </span>
                                {Number(group.created_by) === Number(userId) && (
                                    <span style={{ fontSize: '10px', background: 'var(--cyan-dim)', color: 'var(--cyan)', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>Admin</span>
                                )}
                            </button>
                        ))}
                        {filteredGroups.filter(g => g.is_member).length === 0 && <p style={{ color: 'var(--text-faint)', fontSize: '13px', padding: '0 16px' }}>{groupSearch ? 'No joined groups found.' : "You haven't joined any groups yet."}</p>}

                        <div style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
                            <h3>Discover Groups</h3>
                            {filteredGroups.filter(g => !g.is_member).map(group => (
                                <button
                                    key={group.id}
                                    className={`disc-menu-item ${activeTab === group.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(group.id)}
                                    style={{ justifyContent: 'space-between' }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {group.is_private ? <Lock size={16} /> : <Hash size={16} />}
                                        {group.name}
                                    </span>
                                    {group.has_requested && <span style={{ fontSize: '10px', background: 'var(--yellow-dim)', color: 'var(--yellow)', padding: '2px 6px', borderRadius: '4px', fontWeight: '800' }}>Requested</span>}
                                </button>
                            ))}
                            {filteredGroups.filter(g => !g.is_member).length === 0 && <p style={{ color: 'var(--text-faint)', fontSize: '13px', padding: '0 16px' }}>{groupSearch ? 'No matching groups to discover.' : 'No new groups to discover.'}</p>}
                        </div>

                        {trendingTags.length > 0 && (
                            <div style={{ marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
                                <h3>Trending Topics</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '0 10px' }}>
                                    {trendingTags.map((tag, idx) => (
                                        <button 
                                            key={idx}
                                            onClick={() => setActiveHashtag(tag.name)}
                                            className={`trending-tag-btn ${activeHashtag === tag.name ? 'active' : ''}`}
                                        >
                                            <span>#{tag.name}</span>
                                            <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>{tag.count} posts</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="disc-main" style={embedded ? { maxWidth: '100%', width: '100%', padding: '20px 32px' } : {}}>
                {embedded ? (
                    <div className="disc-header-nav-box">
                        <div className="disc-header-nav-top">
                            <div>
                                <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--proto-white)', margin: '0 0 6px' }}>
                                    {activeTab === 'global' ? 'Global Community Feed' : currentGroup?.name || 'Group Feed'}
                                </h2>
                                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
                                    {activeTab === 'global' ? "See what's happening across the community, explore topics, and engage with peers." : currentGroup?.description}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                {isGroupAdmin && currentGroup?.is_private && requests.length > 0 && (
                                    <div style={{ background: '#eff6ff', padding: '6px 14px', borderRadius: '8px', border: '1px solid #bfdbfe', fontSize: '13px' }}>
                                        <span style={{ color: '#1e40af', fontWeight: 'bold' }}>{requests.length} Pending Request{requests.length > 1 ? 's' : ''}</span>
                                    </div>
                                )}
                                {isMemberOfCurrent && activeTab !== 'global' && (
                                    <button
                                        onClick={handleManageGroup}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: isGroupAdmin ? 'var(--cyan)' : 'var(--graphite-dark)', color: isGroupAdmin ? 'var(--engine-black)' : 'var(--text-muted)', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }}
                                    >
                                        <Users size={16} /> {isGroupAdmin ? 'Manage Group' : 'View Members'}
                                    </button>
                                )}
                                <button
                                    onClick={() => setShowCreateGroup(true)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--cyan)', color: 'var(--engine-black)', border: 'none', padding: '9px 18px', borderRadius: '10px', cursor: 'pointer', fontWeight: '800', fontSize: '13px', boxShadow: '0 4px 14px rgba(25, 211, 255, 0.3)' }}
                                >
                                    <Plus size={16} /> Create New Group
                                </button>
                            </div>
                        </div>

                        <div className="community-dropdown-container">
                            <button 
                                className="community-dropdown-btn"
                                onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                            >
                                {activeTab === 'global' ? (
                                    <><Globe size={18} /> Global Feed</>
                                ) : (
                                    <>{currentGroup?.is_private ? <Lock size={18} /> : <Hash size={18} />} {currentGroup?.name || 'Group'}</>
                                )}
                                <ChevronDown size={16} style={{ marginLeft: '8px', opacity: 0.7, transform: showGroupDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </button>

                            <AnimatePresence>
                                {showGroupDropdown && (
                                    <>
                                        <div className="dropdown-overlay" onClick={() => setShowGroupDropdown(false)} />
                                        <motion.div 
                                            className="community-dropdown-menu"
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.15 }}
                                        >
                                            <div className="dropdown-search-wrapper">
                                                <Search size={14} className="dropdown-search-icon" />
                                                <input 
                                                    type="text" 
                                                    className="dropdown-search-input"
                                                    placeholder="Search groups or batches..."
                                                    value={groupSearch}
                                                    onChange={(e) => setGroupSearch(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                {groupSearch && (
                                                    <button 
                                                        className="dropdown-search-clear"
                                                        onClick={(e) => { e.stopPropagation(); setGroupSearch(''); }}
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="dropdown-section-title">FEEDS</div>
                                            <div className="dropdown-grid">
                                                <button
                                                    className={`dropdown-menu-item ${activeTab === 'global' ? 'active' : ''}`}
                                                    onClick={() => { setActiveTab('global'); setShowGroupDropdown(false); }}
                                                >
                                                    <Globe size={16} /> Global Feed
                                                </button>
                                            </div>

                                            <div className="dropdown-section-title" style={{ marginTop: '12px' }}>YOUR GROUPS</div>
                                            <div className="dropdown-grid">
                                                {filteredGroups.filter(g => g.is_member).map(group => (
                                                    <button
                                                        key={group.id}
                                                        className={`dropdown-menu-item ${activeTab === group.id ? 'active' : ''}`}
                                                        onClick={() => { setActiveTab(group.id); setShowGroupDropdown(false); }}
                                                    >
                                                        {group.is_private ? <Lock size={16} /> : <Hash size={16} />}
                                                        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</span>
                                                        {Number(group.created_by) === Number(userId) && (
                                                            <span className="dropdown-badge">Admin</span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                            {filteredGroups.filter(g => g.is_member).length === 0 && (
                                                <div className="dropdown-empty-text">You haven't joined any groups.</div>
                                            )}

                                            {filteredGroups.filter(g => !g.is_member).length > 0 && (
                                                <>
                                                    <div className="dropdown-section-title" style={{ marginTop: '12px' }}>DISCOVER</div>
                                                    <div className="dropdown-grid">
                                                        {filteredGroups.filter(g => !g.is_member).map(group => (
                                                            <button
                                                                key={group.id}
                                                                className={`dropdown-menu-item ${activeTab === group.id ? 'active' : ''}`}
                                                                onClick={() => { setActiveTab(group.id); setShowGroupDropdown(false); }}
                                                            >
                                                                {group.is_private ? <Lock size={16} /> : <Hash size={16} />}
                                                                <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</span>
                                                                {group.has_requested && (
                                                                    <span className="dropdown-badge requested">Requested</span>
                                                                )}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                ) : (
                    <div className="disc-header">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <h2>{activeTab === 'global' ? 'Global Feed' : currentGroup?.name || 'Group Feed'}</h2>
                                <p style={{ color: 'var(--text-muted)' }}>
                                    {activeTab === 'global' ? "See what's happening across the community." : currentGroup?.description}
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                {isGroupAdmin && currentGroup?.is_private && requests.length > 0 && (
                                    <div style={{ background: '#eff6ff', padding: '8px 16px', borderRadius: '8px', border: '1px solid #bfdbfe', fontSize: '14px' }}>
                                        <span style={{ color: '#1e40af', fontWeight: 'bold' }}>{requests.length} Pending Request{requests.length > 1 ? 's' : ''}</span>
                                    </div>
                                )}
                                {isMemberOfCurrent && activeTab !== 'global' && (
                                    <button
                                        onClick={handleManageGroup}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', background: isGroupAdmin ? 'var(--cyan)' : 'var(--graphite-dark)', color: isGroupAdmin ? 'var(--engine-black)' : 'var(--text-muted)', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}
                                    >
                                        <Users size={16} /> {isGroupAdmin ? 'Manage Group' : 'View Members'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {isGroupAdmin && requests.length > 0 && (
                    <div className="requests-section">
                        <h4>Pending Join Requests</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {requests.map(req => (
                                <div key={req.request_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--graphite-dark)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--cyan-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'var(--cyan)', fontSize: '13px' }}>
                                            {req.user_name ? req.user_name.charAt(0).toUpperCase() : '?'}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '700', color: 'var(--proto-white)' }}>{req.user_name}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Requested to join</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={() => handleRequestAction(req.request_id, 'approve')} style={{ background: 'var(--green)', color: '#0B0F14', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '800', fontSize: '13px' }}>Approve</button>
                                        <button onClick={() => handleRequestAction(req.request_id, 'decline')} style={{ background: 'var(--red)', color: '#0B0F14', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: '800', fontSize: '13px' }}>Decline</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab !== 'global' && !isMemberOfCurrent ? (
                    <div className="join-group-placeholder">
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {currentGroup?.is_private ? <Lock size={40} color="var(--text-muted)" /> : <Hash size={40} color="var(--text-muted)" />}
                        </div>
                        <div>
                            <h3>{currentGroup?.is_private ? "This group is Private" : "Join this Group"}</h3>
                            <p style={{ color: 'var(--text-muted)', maxWidth: '400px' }}>
                                {currentGroup?.is_private ? "You need to request access to view discussions in this group." : "Join this community to start posting and interacting with members."}
                            </p>
                        </div>
                        <button className="join-group-btn" onClick={() => handleJoinGroup(currentGroup.id)} disabled={currentGroup?.has_requested}>
                            {currentGroup?.has_requested ? "Request Sent" : (currentGroup?.is_private ? "Request to Join" : "Join Group")}
                        </button>
                    </div>
                ) : (
                    <>
                        {canPost ? (
                        <div className="post-create-box" style={{ position: 'relative' }}>
                            {/* 1. Hashtag Filter Banner */}
                            {activeHashtag && (
                                <div className="active-hashtag-banner">
                                    Filtering by #{activeHashtag} 
                                    <button onClick={() => setActiveHashtag(null)}>×</button>
                                </div>
                            )}

                            {/* 2. The Textarea */}
                            <textarea 
                                className="post-input" 
                                placeholder={activeTab === 'global' ? "What's on your mind? Use @ to mention or # for tags." : `Post something in ${currentGroup?.name}...`} 
                                value={newPostContent} 
                                onChange={(e) => {
                                    handleContentChange(e.target.value, 'post');
                                    handleLinkDetection(e.target.value);
                                }} 
                            />

                            {/* 3. Mention Dropdown */}
                            {showMentionDropdown && activeInputType === 'post' && (
                                <div className="mention-dropdown">
                                    {mentionResults.map(u => (
                                        <div key={u.id} className="mention-item" onClick={() => insertMention(u)}>
                                            <div className="mention-avatar">{u.name.charAt(0)}</div>
                                            <div className="mention-name">{u.name} <small>({u.type})</small></div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 4. Media Previews (Image, PDF, YouTube, Links) */}
                            {mediaUrl && (
                                <div className="post-media-preview">
                                    {mediaType === 'image' && (
                                        <img src={mediaUrl.startsWith('http') ? mediaUrl : `${process.env.REACT_APP_API_URL}/uploads/${mediaUrl}`} alt="Preview" />
                                    )}
                                    
                                    {mediaType === 'pdf' && (
                                        <div className="media-preview-file"><FileText size={20} /> Preview.pdf</div>
                                    )}
                                    
                                    {mediaType === 'youtube' && (
                                        <div className="media-preview-yt">
                                            <Youtube size={20} color="#ff0000" /> YouTube Video Detected
                                        </div>
                                    )}
                                    
                                    {mediaType === 'link' && linkPreview && (
                                        <div className="media-preview-link">
                                            {linkPreview.image && <img src={linkPreview.image} alt="" className="link-preview-img" />}
                                            <div className="link-preview-info">
                                                <div className="link-title">{linkPreview.title}</div>
                                                <div className="link-desc">{linkPreview.description}</div>
                                                <div className="link-url">
                                                    <LinkIcon size={12} /> {new URL(linkPreview.url).hostname}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Remove Media Button */}
                                    <button className="remove-media" onClick={() => { 
                                        setMediaUrl(""); 
                                        setMediaType(null); 
                                        setLinkPreview(null); 
                                    }}>
                                        <X size={14} />
                                    </button>
                                </div>
                            )}

                            <div className="post-actions">
                                <div className="post-upload-options">
                                    <label className="upload-icon-btn" title="Add Image">
                                        <Image size={20} />
                                        <span>Image</span>
                                        <input type="file" accept="image/*" hidden onChange={(e) => handleFileUpload(e, 'image')} />
                                    </label>
                                    <label className="upload-icon-btn" title="Add Document">
                                        <FileText size={20} />
                                        <span>Document</span>
                                        <input type="file" accept="application/pdf" hidden onChange={(e) => handleFileUpload(e, 'pdf')} />
                                    </label>
                                </div>
                                <button className="post-submit_btn" onClick={handleCreatePost} disabled={isUploading || isFetchingLink || isSubmittingPost}>
                                    {isUploading ? "Uploading..." : isFetchingLink ? "Fetching..." : isSubmittingPost ? "Posting..." : "Post"}
                                </button>
                            </div>
                        </div>
                        ) : (
                            <div className="post-create-box" style={{ padding: '16px 20px', color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '14px' }}>
                                Posting is disabled for your role at your institution. You can still browse the feed.
                            </div>
                        )}

                        <div className="feed-sort-controls">
                            <button 
                                onClick={() => setSortBy('new')}
                                className={`feed-sort-btn ${sortBy === 'new' ? 'active' : ''}`}
                            >
                                <Sparkles size={16} /> New
                            </button>
                            <button 
                                onClick={() => setSortBy('hot')}
                                className={`feed-sort-btn ${sortBy === 'hot' ? 'active' : ''}`}
                            >
                                <Flame size={16} /> Hot
                            </button>
                            <button 
                                onClick={() => setSortBy('top')}
                                className={`feed-sort-btn ${sortBy === 'top' ? 'active' : ''}`}
                            >
                                <TrendingUp size={16} /> Top
                            </button>
                        </div>

                        <div className="disc-feed">
                            {loading ? (
                                <p>Loading discussions...</p>
                            ) : (
                                <AnimatePresence>
                                    {posts.map(post => (
                                        <motion.div key={post.id} className="feed-item" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                                            <div className="feed-header">
                                                <Avatar src={post.user_avatar} name={post.user_name} />
                                                <div className="feed-meta">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <h4>{post.user_name}</h4>
                                                        {post.is_edited && <span className="edited-tag">• Edited</span>}
                                                    </div>
                                                    <span>{new Date(post.created_at).toLocaleDateString()} • {activeTab === 'global' && post.group_id ? `in Group #${post.group_id}` : 'Public'}</span>
                                                </div>
                                                
                                                {(Number(post.user_id) === Number(userId) || isGroupAdmin) && (
                                                    <div className="post-options-container">
                                                        <button className="post-options-trigger" onClick={() => setActiveMenuId(activeMenuId === post.id ? null : post.id)}>
                                                            <MoreVertical size={18} />
                                                        </button>
                                                        {activeMenuId === post.id && (
                                                            <div className="post-options-menu">
                                                                {post.user_id === userId && (
                                                                    <button onClick={() => handleStartEdit(post)}>
                                                                        <Edit2 size={14} /> Edit Post
                                                                    </button>
                                                                )}
                                                                <button className="delete-opt" onClick={() => handleDeletePost(post.id)}>
                                                                    <Trash2 size={14} /> Delete Post
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {editingPostId === post.id ? (
                                                <div className="edit-post-area">
                                                    <textarea 
                                                        className="edit-post-input" 
                                                        value={editContent} 
                                                        onChange={(e) => setEditContent(e.target.value)} 
                                                    />
                                                    <div className="edit-post-actions">
                                                        <button className="cancel-edit" onClick={() => setEditingPostId(null)}>Cancel</button>
                                                        <button className="save-edit" onClick={() => handleSaveEdit(post.id)}>Save</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="feed-content">
                                                    <RichText text={post.content} onHashtagClick={handleHashtagClick} />
                                                    
                                                    {post.media_url && (
                                                        <div className="post-media-content">
                                                            {post.media_type === 'image' && (
                                                                <img 
                                                                    src={post.media_url.startsWith('http') ? post.media_url : `${process.env.REACT_APP_API_URL}/uploads/${post.media_url}`} 
                                                                    alt="Post Media" 
                                                                    className="post-image" 
                                                                />
                                                            )}
                                                            {post.media_type === 'pdf' && (
                                                                <a 
                                                                    href={post.media_url.startsWith('http') ? post.media_url : `${process.env.REACT_APP_API_URL}/uploads/${post.media_url}`} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="post-pdf-link"
                                                                >
                                                                    <FileText size={20} />
                                                                    <span>View Document (PDF)</span>
                                                                </a>
                                                            )}
                                                            {post.media_type === 'youtube' && (
                                                                <div className="post-youtube-embed">
                                                                    <iframe
                                                                        width="100%"
                                                                        height="315"
                                                                        src={`https://www.youtube.com/embed/${post.media_url}`}
                                                                        title="YouTube video player"
                                                                        frameBorder="0"
                                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                        allowFullScreen
                                                                    ></iframe>
                                                                </div>
                                                            )}
                                                            {post.media_type === 'link' && post.link_preview_data && (
                                                                <a href={post.media_url} target="_blank" rel="noopener noreferrer" className="post-content-link-preview">
                                                                    {post.link_preview_data.image && (
                                                                        <div className="link-preview-image">
                                                                            <img src={post.link_preview_data.image} alt="" />
                                                                        </div>
                                                                    )}
                                                                    <div className="link-preview-body">
                                                                        <div className="link-preview-title">{post.link_preview_data.title}</div>
                                                                        <div className="link-preview-description">{post.link_preview_data.description}</div>
                                                                        <div className="link-preview-site">
                                                                            <LinkIcon size={12} /> {new URL(post.media_url).hostname}
                                                                        </div>
                                                                    </div>
                                                                </a>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <div className="feed-footer">
                                                <button className={`feed-action ${post.is_liked ? 'active' : ''}`} onClick={() => handleLike(post.id)}>
                                                    <Heart size={18} fill={post.is_liked ? "currentColor" : "none"} />
                                                    {post.likes_count} Likes
                                                </button>
                                                <button className="feed-action" onClick={() => toggleComments(post.id)}>
                                                    <MessageSquare size={18} />
                                                    {post.comments_count} Comment{post.comments_count !== 1 ? 's' : ''}
                                                </button>

                                                <div className="post-footer-menu-container">
                                                    <button 
                                                        className="feed-action post-footer-menu-trigger" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenPostMenuId(openPostMenuId === post.id ? null : post.id);
                                                        }}
                                                        title="More options"
                                                    >
                                                        <MoreVertical size={18} />
                                                    </button>
                                                    {openPostMenuId === post.id && (
                                                        <div className="post-footer-dropdown-menu">
                                                            <button 
                                                                className="dropdown-item report-opt"
                                                                onClick={() => openReportModal('post', post.id)}
                                                            >
                                                                <Flag size={14} /> Report
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {expandedComments[post.id] && (
                                                <div className="feed-comments-section">
                                                    {canPost ? (
                                                    <div className="comment-input-area" style={{ position: 'relative' }}>
                                                        <input 
                                                            type="text" 
                                                            className="comment-input" 
                                                            placeholder="Add a comment..." 
                                                            value={newComment[post.id] || ''} 
                                                            onChange={(e) => handleContentChange(e.target.value, 'comment', post.id)} 
                                                            onKeyPress={(e) => { if (e.key === 'Enter') handleAddComment(post.id); }} 
                                                        />
                                                        {showMentionDropdown && activeInputType === 'comment' && activeCommentId === post.id && (
                                                            <MentionDropdown results={mentionResults} onSelect={insertMention} />
                                                        )}
                                                    </div>
                                                    ) : null}
                                                    {loadingComments[post.id] ? (
                                                        <p style={{ color: 'var(--text-faint)', fontSize: '14px' }}>Loading comments...</p>
                                                    ) : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            {(comments[post.id] || []).map(comment => (
                                                                    <CommentItem 
                                                                    key={comment.id} 
                                                                    comment={comment} 
                                                                    postId={post.id} 
                                                                    userId={userId} 
                                                                    isGroupAdmin={isGroupAdmin}
                                                                    handleDeleteComment={handleDeleteComment}
                                                                    handleAddComment={handleAddComment}
                                                                    newComment={newComment}
                                                                    handleContentChange={handleContentChange}
                                                                    fetchComments={fetchComments}
                                                                    loadingComments={loadingComments}
                                                                    onHashtagClick={handleHashtagClick}
                                                                    showMentionDropdown={showMentionDropdown}
                                                                    mentionResults={mentionResults}
                                                                    insertMention={insertMention}
                                                                    activeInputType={activeInputType}
                                                                    activeCommentId={activeCommentId}
                                                                    onReportComment={(commentId, type = 'comment') => openReportModal(type, commentId)}
                                                                    canPost={canPost}
                                                                />
                                                            ))}
                                                            {(comments[post.id] || []).length === 0 && <p style={{ color: 'var(--text-faint)', fontSize: '14px', textAlign: 'center', padding: '12px' }}>No comments yet.</p>}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                    {hasMore && (
                                        <div className="feed-load-more-container" ref={loaderRef}>
                                            <button 
                                                className="feed-load-more-btn"
                                                onClick={() => fetchPosts(true)}
                                                disabled={isLoadingMore || loading}
                                            >
                                                {isLoadingMore ? (
                                                    <>
                                                        <Loader2 size={16} className="spin-icon" />
                                                        <span>Loading more posts...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <ChevronDown size={16} />
                                                        <span>Load More Posts</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </AnimatePresence>
                            )}
                            {!loading && posts.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-faint)' }}>
                                    <MessageSquare size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
                                    <p>No discussions yet.</p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {showCreateGroup && (
                <div className="modal-overlay">
                    <motion.div className="modal-box" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
                        <div className="modal-header">
                            <h3>Create New Group</h3>
                            <button onClick={() => setShowCreateGroup(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
                        </div>
                        <div style={{ width: '100%', boxSizing: 'border-box' }}>
                            <div style={{ width: '100%', boxSizing: 'border-box', marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Group Name</label>
                                <input type="text" value={newGroupData.name} onChange={(e) => setNewGroupData({ ...newGroupData, name: e.target.value })} placeholder="e.g., Python Enthusiasts" style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-soft)', color: 'var(--proto-white)', fontSize: '0.95rem', outline: 'none' }} />
                            </div>
                            <div style={{ width: '100%', boxSizing: 'border-box', marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</label>
                                <textarea style={{ width: '100%', boxSizing: 'border-box', minHeight: '100px', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-soft)', color: 'var(--proto-white)', fontSize: '0.95rem', outline: 'none', resize: 'vertical' }} value={newGroupData.description} onChange={(e) => setNewGroupData({ ...newGroupData, description: e.target.value })} placeholder="What is this group about?" />
                            </div>
                            <div style={{ width: '100%', boxSizing: 'border-box', marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Select Batch</label>
                                <select 
                                    value={newGroupData.batch || ''} 
                                    onChange={(e) => setNewGroupData({ ...newGroupData, batch: e.target.value })} 
                                    style={{ width: '100%', boxSizing: 'border-box', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-soft)', color: 'var(--proto-white)', fontSize: '0.95rem', outline: 'none', cursor: 'pointer' }}
                                >
                                    <option value="">Select Batch</option>
                                    <option value="All Batches">All Batches</option>
                                    {availableBatches.map((b, idx) => (
                                        <option key={idx} value={b.value}>{b.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', boxSizing: 'border-box', marginTop: '10px', marginBottom: '24px', padding: '0 4px' }}>
                                <input type="checkbox" id="is_private" checked={newGroupData.is_private} onChange={(e) => setNewGroupData({ ...newGroupData, is_private: e.target.checked })} style={{ width: '16px', height: '16px', cursor: 'pointer', margin: 0 }} />
                                <label htmlFor="is_private" style={{ margin: 0, cursor: 'pointer', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Private Group</label>
                            </div>
                            <button onClick={handleCreateGroup} style={{ width: '100%', boxSizing: 'border-box', background: 'var(--cyan)', color: 'var(--engine-black)', border: 'none', padding: '14px', borderRadius: '10px', cursor: 'pointer', fontWeight: '800', fontSize: '15px', boxShadow: '0 4px 14px rgba(25, 211, 255, 0.3)' }}>Create Group</button>
                        </div>
                    </motion.div>
                </div>
            )}

            {showManageGroup && (
                <div className="modal-overlay">
                    <motion.div className="modal-box" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h3>{isGroupAdmin ? 'Manage' : 'View'} Group: {currentGroup?.name}</h3>
                            <button onClick={() => setShowManageGroup(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
                        </div>
                        <div className="modal-content">
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ marginBottom: '12px' }}>Group Members ({groupMembers.length})</h4>
                                <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {groupMembers.map(member => (
                                        <div key={member.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'var(--engine-black)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <Avatar src={member.user_avatar} name={member.user_name} />
                                                <div>
                                                    <div style={{ fontWeight: '600', color: 'var(--proto-white)' }}>{member.user_name} {member.role === 'Admin' && <span style={{ fontSize: '10px', background: 'var(--yellow)', color: '#0B0F14', padding: '2px 4px', borderRadius: '4px', fontWeight: '800', marginLeft: '6px' }}>Admin</span>}</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{member.user_type} • Joined {new Date(member.joined_at).toLocaleDateString()}</div>
                                                </div>
                                            </div>
                                            {isGroupAdmin && member.user_id !== userId && (
                                                <button onClick={() => handleRemoveMember(member.user_id)} style={{ background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid var(--red-dim)', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>Remove</button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {isGroupAdmin && (
                                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                                    <button onClick={handleDeleteGroup} style={{ width: '100%', background: 'var(--red)', color: '#0B0F14', border: 'none', padding: '14px', borderRadius: '12px', cursor: 'pointer', fontWeight: '800' }}>Delete Group Permanently</button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
            </div>
        );

    if (embedded) return content;
    const userRole = localStorage.getItem('role') || '';
    if (userRole.toLowerCase() === 'student') {
        return <StudentLayout>{content}</StudentLayout>;
    }
    return content;
};

export default DiscussionsPage;
